// src/hooks/useAuth.tsx
import { createContext, useContext, useCallback, useMemo, useState, type ReactNode } from 'react'
import { postLogin } from '../services/api'

export type Plan = 'basic' | 'pro' | 'deluxe' | string

type StoredUser = { id: number | string; name: string; email: string } | null

type AuthState = {
  email: string | null
  token: string | null
  plan: Plan | null
  user: StoredUser
}

type LoginPayload = {
  user: { id: string | number; name: string; email: string }
  token: string
  plan: Plan
}

type AuthContextValue = AuthState & {
  isAuthenticated: boolean
  // Overloaded login: either credentials OR legacy payload style
  login: {
    (email: string, password: string): Promise<void>
    (payload: LoginPayload): Promise<void>
  }
  logout: () => void
}

const TOKEN_KEY = 'quiz_token'
const EMAIL_KEY = 'quiz_email'
const PLAN_KEY = 'quiz_plan'
const USER_KEY = 'quiz_user'
const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    if (typeof window === 'undefined') {
      return { email: null, token: null, plan: null, user: null }
    }
    try {
      const token = localStorage.getItem(TOKEN_KEY)
      const email = localStorage.getItem(EMAIL_KEY)
      const plan = localStorage.getItem(PLAN_KEY)
      const rawUser = localStorage.getItem(USER_KEY)
      return {
        token,
        email,
        plan: plan ?? null,
        user: rawUser ? (JSON.parse(rawUser) as StoredUser) : null,
      }
    } catch {
      return { email: null, token: null, plan: null, user: null }
    }
  })

  const persist = useCallback((next: AuthState) => {
    setState(next)
    if (typeof window === 'undefined') return
    if (next.token) {
      localStorage.setItem(TOKEN_KEY, next.token)
      if (next.email) localStorage.setItem(EMAIL_KEY, next.email)
      if (next.plan) localStorage.setItem(PLAN_KEY, next.plan)
      if (next.user) {
        localStorage.setItem(USER_KEY, JSON.stringify(next.user))
      } else {
        localStorage.removeItem(USER_KEY)
      }
    } else {
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(EMAIL_KEY)
      localStorage.removeItem(PLAN_KEY)
      localStorage.removeItem(USER_KEY)
    }
  }, [])

  const applyLoginResponse = useCallback(
    (payload: { token: string; email: string; plan: Plan | null; user: StoredUser }) => {
      persist({
        token: payload.token,
        email: payload.email,
        plan: payload.plan,
        user: payload.user,
      })
    },
    [persist]
  )

  const login = useCallback(
    async (a: string | LoginPayload, b?: string): Promise<void> => {
      if (typeof a === 'string') {
        const email = a.trim().toLowerCase()
        const password = (b ?? '').trim()
        const data = await postLogin({ email, password })
        applyLoginResponse({
          token: data.access_token,
          email: data.user?.email ?? email,
          plan: (data.plan as Plan | undefined) ?? null,
          user: data.user ?? { id: email, name: email, email },
        })
        return
      }

      const payload = a as LoginPayload
      applyLoginResponse({
        token: payload.token,
        email: payload.user.email,
        plan: payload.plan ?? null,
        user: payload.user,
      })
    },
    [applyLoginResponse]
  )

  const logout = useCallback(() => {
    persist({ email: null, token: null, plan: null, user: null })
  }, [persist])

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      isAuthenticated: !!state.token,
      login,
      logout,
    }),
    [state, login, logout]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}
